import {PutObjectCommand, S3Client} from '@aws-sdk/client-s3'

import {
  AWS_ACCESS_KEY_ID,
  AWS_REGION,
  AWS_S3_BUCKET_NAME,
  AWS_SECRET_ACCESS_KEY,
  AWS_SESSION_TOKEN
} from '../constants/aws'
import {UploadCssThemeRequest, UploadCssThemeResponse} from '../types/s3Upload'

// AWS Configuration - hardcoded for developer use
// Developers should add their actual AWS credentials here
const AWS_CONFIG = {
  accessKeyId: AWS_ACCESS_KEY_ID,
  secretAccessKey: AWS_SECRET_ACCESS_KEY,
  sessionToken: AWS_SESSION_TOKEN,
  region: AWS_REGION,
  bucketName: AWS_S3_BUCKET_NAME
}

/**
 * Sanitizes a theme name for use in S3 keys and URLs
 * @param themeName - Raw theme name input
 * @returns Sanitized theme name with only alphanumeric characters, hyphens, and underscores
 * @internal
 */
const sanitizeThemeName = (themeName: string): string => {
  return themeName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Generates a timestamp in milliseconds since epoch
 * @returns Timestamp string in milliseconds
 * @internal
 */
const generateTimestamp = (): string => {
  return Date.now().toString()
}
const timestamp = generateTimestamp()

/**
 * Generates the S3 key for storing a CSS file with timestamp
 * @param themeName - Theme name to include in the key
 * @returns Complete S3 key for the CSS file with timestamp
 * @internal
 */
const getS3Filename = (themeName: string): string => {
  const sanitizedThemeName = sanitizeThemeName(themeName)
  return `${sanitizedThemeName}-${timestamp}.css`
}

/**
 * Generates the theme override URL for accessing an uploaded CSS theme
 * @param themeName - Theme name for the theme
 * @returns Complete theme override URL
 * @internal
 */
const generateThemeOverrideUrl = (themeName: string): string => {
  const sanitizedThemeName = sanitizeThemeName(themeName)
  return `tst.vio.com/?themeOverride=${sanitizedThemeName}-${timestamp}`
}

/**
 * Creates and configures an S3 client using hardcoded credentials
 * @returns Configured S3 client or null if credentials are missing
 * @internal
 */
const createS3Client = (): S3Client | null => {
  const {accessKeyId, secretAccessKey, sessionToken, region} = AWS_CONFIG

  if (!accessKeyId || !secretAccessKey) {
    console.error('S3 upload skipped (no credentials configured)')
    return null
  }

  return new S3Client({
    region,
    credentials: {
      accessKeyId,
      secretAccessKey,
      ...(sessionToken && {sessionToken})
    }
  })
}

/**
 * Uploads a CSS theme file to S3 with the specified brand name
 * @param request - Upload request containing brand name and CSS content
 * @returns Promise that resolves to the upload response
 * @public
 */
export const uploadCssThemeS3 = async (
  request: UploadCssThemeRequest
): Promise<UploadCssThemeResponse> => {
  try {
    const {themeName, cssContent} = request

    // Validate inputs
    if (!themeName?.trim() || !cssContent?.trim()) {
      return {
        success: false,
        message: 'Theme name and CSS content are required',
        error: 'Missing required fields'
      }
    }

    // Create S3 client
    const s3Client = createS3Client()
    if (!s3Client) {
      return {
        success: false,
        message: 'S3 upload failed: Missing AWS credentials',
        error: 'AWS credentials not configured'
      }
    }

    // Generate S3 key and URLs
    const s3Filename = getS3Filename(themeName)
    const themeOverrideUrl = generateThemeOverrideUrl(themeName)

    // Upload to S3
    const putObjectCommand = new PutObjectCommand({
      Bucket: AWS_CONFIG.bucketName,
      Key: s3Filename,
      Body: cssContent,
      ContentType: 'text/css',
      CacheControl: 'max-age=31536000' // 1 year cache
    })

    await s3Client.send(putObjectCommand)

    return {
      success: true,
      message: `Successfully uploaded CSS theme for "${themeName}"`,
      data: {
        s3Filename,
        themeOverrideUrl
      }
    }
  } catch (error) {
    console.error('S3 upload error:', error)

    return {
      success: false,
      message: 'Failed to upload CSS theme to S3',
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}
